'use client';

import type { PageTableOfContentsProps } from '@routeroyal/theme-core';
import { motion } from 'framer-motion';

/**
 * AllysPageTableOfContents - Allys-themed navigation sidebar for long pages
 * 
 * Displays a list of headings extracted from the page content,
 * with active heading highlighting and Allys navy/gold styling.
 */
export function AllysPageTableOfContents({
  headings,
  activeId,
  className = '',
  title = 'On this page',
}: PageTableOfContentsProps) {
  if (headings.length === 0) return null;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      // Update URL hash without scrolling
      window.history.pushState(null, '', `#${id}`);
    }
  };

  return (
    <motion.nav
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6, delay: 0.4 }}
      className={`bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 border border-gray-200 [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] ${className}`}
      aria-label="Table of contents"
    >
      <h3 className="text-xs font-bold text-[#BBA339] uppercase tracking-wider mb-4">
        {title}
      </h3>
      <ul className="space-y-1">
        {headings.map(({ id, text, level }) => (
          <li
            key={id}
            style={{ paddingLeft: `${(level - 2) * 12}px` }}
          >
            <a
              href={`#${id}`}
              onClick={(e) => handleClick(e, id)}
              className={`
                block text-sm py-1.5 px-3 rounded-lg transition-all duration-300
                ${activeId === id
                  ? 'text-[#243462] font-semibold bg-[#BBA339]/10 border-l-2 border-[#BBA339]'
                  : 'text-gray-600 hover:text-[#243462] hover:bg-gray-100'
                }
              `}
            >
              {text}
            </a>
          </li>
        ))}
      </ul>
    </motion.nav>
  );
}
